import { RefObject } from "react";
import { useTranslation } from "react-i18next";
import Spinner from "../../../components/common/Spinner";
import { FileUploadManagerRef } from "../../../components/forms/fileUploadManager";
import { IOrganizationFeeTemplate } from "../../../setup/api/services/organization/models";
import { IOrganizations } from "../../../setup/types/Organisation";
import { TOrganizationPaymentMethods } from "./api";
import { IFeeEstimate } from "./fees";
import Header from "./header";
import Stepper from "./stepper";
import StepDetails from "./stepDetails";
import StepParties from "./stepParties";
import StepValue from "./stepValue";
import Success from "./success";
import NavButtons from "./ui/NavButtons";
import styles from "./styles.module.scss";

export const SUCCESS_STEP = 3;

export interface ITwoPartyWizardViewProps {
  currentStep: number;
  loading: boolean;
  saving: boolean;
  saveErrors: string[];
  nextDisabled: boolean;
  organization?: IOrganizations;
  organizationMethods: TOrganizationPaymentMethods;
  feeTemplate?: IOrganizationFeeTemplate;
  estimate?: IFeeEstimate;
  fileUploadRef: RefObject<FileUploadManagerRef>;
  onUploadStatusChange: (isUploading: boolean) => void;
  header: { isDirty: boolean; discardId?: string; onLeave: () => void };
  success: {
    buyerName: string;
    shareLink: string;
    amount: number;
    onViewDetails: () => void;
  };
  onNext: () => void;
  onPrev?: () => void;
  /** Absent while editing a published transaction: there is no draft to save. */
  onSaveDraft?: () => void;
  /** Editing a published transaction the buyer has not paid yet. */
  editMode?: boolean;
}

/**
 * The two-party wizard as drawn (Figma nodes 19739:126479 … 19739:127563). Holds no data of its
 * own: the page feeds it, and so can a preview with sample data. Expects a FormProvider above it.
 */
const TwoPartyWizardView = ({
  currentStep,
  loading,
  saving,
  saveErrors,
  nextDisabled,
  organization,
  organizationMethods,
  feeTemplate,
  estimate,
  fileUploadRef,
  onUploadStatusChange,
  header,
  success,
  onNext,
  onPrev,
  onSaveDraft,
  editMode,
}: ITwoPartyWizardViewProps) => {
  const { t } = useTranslation();

  return (
    <div className={styles.container}>
      <Header {...header} />

      {loading ? (
        <div className={styles.loading}>
          <Spinner />
        </div>
      ) : currentStep === SUCCESS_STEP ? (
        <Success {...success} estimate={estimate} />
      ) : (
        <form
          className={styles.content}
          aria-busy={saving}
          onSubmit={(event) => event.preventDefault()}>
          <div className={styles.form}>
            <Stepper currentStep={currentStep} editMode={editMode} />

            {saveErrors.length > 0 && (
              <div className={styles.saveErrors} role="alert">
                <b>{t("err_two_party_save_title")}</b>
                <ul>
                  {saveErrors.map((message, index) => (
                    <li key={index}>{message}</li>
                  ))}
                </ul>
              </div>
            )}

            {currentStep === 0 && (
              <StepDetails
                fileUploadRef={fileUploadRef}
                onUploadStatusChange={onUploadStatusChange}
              />
            )}
            {currentStep === 1 && <StepParties organization={organization} />}
            {currentStep === 2 && (
              <StepValue
                organizationMethods={organizationMethods}
                feeTemplate={feeTemplate}
                estimate={estimate}
              />
            )}
          </div>

          <div className={styles.navRow}>
            {saving && (
              <div className={styles.savingOverlay}>
                <Spinner />
              </div>
            )}
            <NavButtons
              disabled={nextDisabled}
              onNext={onNext}
              onPrev={onPrev}
              onSaveDraft={onSaveDraft}
              nextLabel={
                editMode && currentStep === 2
                  ? t("lbl_save_changes")
                  : undefined
              }
            />
          </div>
        </form>
      )}
    </div>
  );
};

export default TwoPartyWizardView;
